import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { tiers } from '../data/content'
import Button from './Button'
import { duration, ease, stagger } from '../lib/motion'
import { useFormModal } from '../context/FormModalContext'

function scrollToInquiry() {
  document.getElementById('inquiry')?.scrollIntoView({ behavior: 'smooth' })
}

// Two ways to get a website — a build-type toggle, not a page navigation.
// Swaps only the panel below it (paid tiers <-> free-build explainer);
// everything else on the page, including BWC Care Plans further down,
// is completely unaffected by which one is selected. Defaults to "paid"
// since that's the primary offer for most visitors.
const BUILD_PATHS = [
  { id: 'paid', label: 'I want to pay for a website' },
  { id: 'free', label: 'I want a free website' },
]

export default function Pricing({ onSelect, ctaLabel }) {
  const [buildPath, setBuildPath] = useState('paid')
  const { openFreeBuild } = useFormModal()

  return (
    <section id="pricing" className="py-24 border-t" style={{ borderColor: 'var(--border)' }}>
      <div className="container-bw">
        <div className="max-w-xl mb-10">
          <p className="eyebrow mb-4">Pricing</p>
          <h2 className="font-display text-3xl md:text-4xl mb-4" style={{ color: 'var(--text-primary)' }}>
            Three ways to own your online presence.
          </h2>
          <p style={{ color: 'var(--text-secondary)' }}>
            Every business is at a different stage. Choose the starting point that matches
            yours. Every project is scoped to your actual goals, not a fixed template.
          </p>
        </div>

        <div
          role="tablist"
          aria-label="How do you want to get your website?"
          className="flex flex-col sm:inline-flex sm:flex-row w-full sm:w-auto items-stretch sm:items-center gap-1 rounded-2xl sm:rounded-full p-1 border mb-10"
          style={{ borderColor: 'var(--border)', background: 'var(--bg-elevated)' }}
        >
          {BUILD_PATHS.map((path) => {
            const active = buildPath === path.id
            return (
              <button
                key={path.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setBuildPath(path.id)}
                className="relative w-full sm:w-auto px-4 py-2 rounded-xl sm:rounded-full text-sm font-medium text-center focus-visible:outline-none"
                style={{
                  color: active ? 'var(--accent-text)' : 'var(--text-secondary)',
                  background: active ? 'var(--accent)' : 'transparent',
                  transition: 'background-color 280ms ease, color 280ms ease',
                }}
              >
                {path.label}
              </button>
            )
          })}
        </div>

        <AnimatePresence mode="wait">
          {buildPath === 'paid' ? (
            <motion.div
              key="paid"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: duration.fast, ease: ease.out }}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
                {tiers.map((tier, i) => (
                  <motion.div
                    key={tier.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: duration.base, delay: i * stagger.base }}
                    whileHover={{ y: -4 }}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      onSelect(tier.id)
                      scrollToInquiry()
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onSelect(tier.id)
                        scrollToInquiry()
                      }
                    }}
                    className="relative text-left rounded-2xl border p-8 flex flex-col h-full cursor-pointer focus-visible:outline-none"
                    style={{
                      borderColor: tier.featured ? 'var(--accent)' : 'var(--border)',
                      background: 'var(--card)',
                    }}
                  >
                    {tier.badge && (
                      <span
                        className="absolute -top-3 left-8 text-[11px] font-medium px-3 py-1 rounded-full"
                        style={{ background: 'var(--accent)', color: 'var(--accent-text)' }}
                      >
                        {tier.badge}
                      </span>
                    )}

                    <h3 className="font-display text-2xl mb-2" style={{ color: 'var(--text-primary)' }}>
                      {tier.name}
                    </h3>
                    {tier.for && (
                      <p className="text-sm mb-3" style={{ color: 'var(--text-primary)', opacity: 0.85 }}>
                        {tier.for}
                      </p>
                    )}
                    <p className="text-sm font-mono mb-6" style={{ color: 'var(--accent)' }}>
                      {tier.price}
                      {tier.usdPrice && (
                        <span style={{ color: 'var(--text-muted)' }}> · {tier.usdPrice}</span>
                      )}
                    </p>
                    <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
                      {tier.description}
                    </p>

                    <ul className="flex flex-col gap-2 mb-8 flex-1">
                      {tier.examples.map((ex) => (
                        <li key={ex} className="text-sm" style={{ color: 'var(--text-muted)' }}>
                          {ex}
                        </li>
                      ))}
                    </ul>

                    <span
                      className="mt-auto inline-flex items-center justify-center rounded-xl px-6 py-3 text-sm font-medium"
                      style={
                        tier.featured
                          ? { background: 'var(--accent)', color: 'var(--accent-text)' }
                          : { background: 'transparent', color: 'var(--text-primary)', border: '1px solid var(--border-strong)' }
                      }
                    >
                      {ctaLabel || tier.cta}
                    </span>
                    <p className="mt-4 text-xs" style={{ color: 'var(--text-muted)' }}>
                      Continue growing after launch with a{' '}
                      <a
                        href="#care-plans"
                        onClick={(e) => e.stopPropagation()}
                        className="underline underline-offset-2"
                        style={{ color: 'var(--text-muted)' }}
                      >
                        Care Plan
                      </a>
                      .
                    </p>
                  </motion.div>
                ))}
              </div>

              <p className="mt-10 text-xs max-w-xl" style={{ color: 'var(--text-muted)' }}>
                Every project is tailored to your business. The prices above are starting points. Your
                final investment depends on your goals, required functionality and project scope.
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="free"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: duration.fast, ease: ease.out }}
              className="max-w-xl rounded-2xl border p-8"
              style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
            >
              <p className="eyebrow mb-3">The free-build programme</p>
              <h3 className="font-display text-2xl mb-4" style={{ color: 'var(--text-primary)' }}>
                Free website, in exchange for a quick review.
              </h3>
              <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                We'll build you a complete website at no cost. In return, we just ask for a
                short video review of your experience once it's done, and if you love the
                result, an introduction to other business owners who might too.
              </p>
              <p className="text-sm mb-8" style={{ color: 'var(--text-secondary)' }}>
                No hidden costs, no catch. It's the same quality build as everything else we
                make, just funded by word-of-mouth instead of an invoice.
              </p>
              <Button onClick={openFreeBuild} variant="secondary">
                Ask About the Free Build
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  )
}
